import { useCallback, useEffect, useRef } from "react";
import { useRequest } from "hooks/useRequest";
import api from "utils/api";
import type { UseSeenRoutesArgs, UseSeenRoutesResult } from "./types";

const EMPTY_SET = new Set<string>();

export const useSeenRoutes = ({
  routeTypeId,
}: UseSeenRoutesArgs): UseSeenRoutesResult => {
  const cacheRef = useRef<Map<number, Set<string>>>(new Map());

  const fetchSeenIds = useCallback(
    async (id: number): Promise<Set<string>> => {
      const cached = cacheRef.current.get(id);
      if (cached) return cached;

      const response: { routeIds: number[] } = await api.getSeenRouteIds(id);
      const ids = new Set(response.routeIds.map(String));
      cacheRef.current.set(id, ids);
      return ids;
    },
    [],
  );

  const { data, isLoading, run, mutateData } = useRequest(fetchSeenIds, {
    manual: true,
  });

  const seenIds = routeTypeId ? (data ?? EMPTY_SET) : EMPTY_SET;

  const markSeen = useCallback(
    (routeId: string) => {
      mutateData({
        updater: (prev) => {
          if (prev.has(routeId)) return prev;
          const next = new Set(prev);
          next.add(routeId);

          if (routeTypeId) {
            cacheRef.current.set(routeTypeId, next);
          }

          return next;
        },
      });

      const numericId = parseInt(routeId, 10);
      if (!isNaN(numericId)) {
        api.markRouteSeen(numericId).catch(() => {
          // Best-effort — marking seen is not critical
        });
      }
    },
    [routeTypeId, mutateData],
  );

  useEffect(() => {
    if (!routeTypeId) return;
    run(routeTypeId);
  }, [routeTypeId, run]);

  return { seenIds, isLoading, markSeen };
};
